import React from 'react';
import Header from 'components/layout/Header';
import styled from 'styled-components';
import Footer from 'components/layout/Footer';
import NavAcercaDe from 'components/layout/NavAcercaDe';
import { Divider } from '@material-ui/core';

const Page = styled.div`
  background-color: #f2f4f7;
`;

const Wrapper = styled.main`
  max-width: 1120px;
  margin: 0 auto 34px;
  padding: 0 24px;
`;

const Article = styled.article`
  background-color: white;
  border: 1px solid #e4e7f0;
  border-radius: 6px;
  padding: 38px 44px 44px;
  color: #26384f;
  line-height: 1.65;

  @media(max-width: 720px) {
    padding: 28px 22px;
  }
`;

const Hero = styled.header`
  border-bottom: 1px solid #e4e7f0;
  margin-bottom: 28px;
  padding-bottom: 22px;

  h1 {
    color: var(--primary-color);
    font-family: 'Montserrat', sans-serif;
    font-size: 30px;
    line-height: 1.2;
    margin: 0 0 14px;
  }

  p {
    color: #405674;
    font-size: 16px;
    margin: 0;
    max-width: 920px;
  }
`;

const Section = styled.section`
  margin-top: 30px;

  h2 {
    color: var(--primary-color);
    font-size: 19px;
    margin: 0 0 14px;
  }

  p {
    margin: 0 0 14px;
  }

  a {
    color: var(--primary-color);
    font-weight: 700;
  }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media(max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const ListCard = styled.div`
  border: 1px solid #e4e7f0;
  border-radius: 6px;
  padding: 18px 20px;
  background-color: #fbfcfe;

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const References = styled.ol`
  margin: 0;
  padding-left: 22px;

  li {
    margin-bottom: 13px;
    padding-left: 4px;
  }
`;

const FooterWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const AcercaDe = () => (
  <Page>
    <Header />
    <NavAcercaDe />
    <Wrapper>
      <Article>
        <Hero>
          <h1>Descripción de herramienta de simulación</h1>
          <p>
            Las simulaciones de esta página web fueron realizadas utilizando el Modelo Energético PMR
            desarrollado por el <a href="https://centroenergia.cl/">Centro de Energía de la Universidad de Chile</a>.
            Este modelo permite evaluar el impacto combinado de medidas de mitigación que contribuyen
            a la meta de carbono neutralidad hacia el año 2050.
          </p>
        </Hero>

        <Section>
          <h2>Ejemplo de instrumentos y medidas de mitigación que se pueden modelar</h2>
          <CardGrid>
            <ListCard>
              <ul>
                <li>Impuesto a las emisiones de gases de efecto invernadero.</li>
                <li>Sistema de Permisos de Emisiones Transables.</li>
                <li>Sistema de Norma de Emisiones propuesto en Ley de Cambio Climático.</li>
                <li>Presupuesto de emisiones.</li>
                <li>Límite de emisiones sectoriales.</li>
                <li>Eficiencia energética.</li>
              </ul>
            </ListCard>
            <ListCard>
              <ul>
                <li>Electromovilidad en vehículos particulares, taxis y transporte público.</li>
                <li>Hidrógeno en transporte y procesos industriales.</li>
                <li>Electrificación de sistemas de calefacción.</li>
                <li>Retiro de centrales a carbón y gas natural.</li>
                <li>Introducción de sistemas de almacenamiento.</li>
                <li>Transporte no motorizado.</li>
              </ul>
            </ListCard>
          </CardGrid>
        </Section>

        <Section>
          <h2>Principales salidas del modelo</h2>
          <ListCard>
            <ul>
              <li>Proyección de oferta de generación eléctrica por central y tipo de tecnología.</li>
              <li>Proyección de demanda de energía para todos los sectores del Balance Nacional de Energía.</li>
              <li>Proyección de demanda eléctrica.</li>
              <li>Proyección de capacidad instalada.</li>
              <li>Proyección de emisiones de gases de efecto invernadero (GEI).</li>
              <li>Proyección de costos de inversión, operación y mantenimiento para los sectores del Balance de Energía.</li>
            </ul>
          </ListCard>
        </Section>

        <Section>
          <h2>Características del modelo</h2>
          <ListCard>
            <ul>
              <li>Modelo matemático basado en enfoque de optimización.</li>
              <li>Modelo integrado de oferta y demanda energética.</li>
              <li>Proyección de demanda para transporte, industria y minería, comercial, público y residencial.</li>
              <li>Proyección de oferta considerando fuentes y tecnologías como solar FV, CSP, eólica, almacenamiento, hidroelectricidad y geotermia.</li>
              <li>Representación de restricciones de corto plazo del sector generación eléctrica.</li>
              <li>Proyección de energéticos primarios y secundarios: diésel, gasolina, kerosene, gas natural, gas licuado, carbón, electricidad e hidrógeno.</li>
              <li>Proyección de emisiones basada en metodologías del Inventario de Gases de Efecto Invernadero.</li>
            </ul>
          </ListCard>
        </Section>

        <Section>
          <h2>Contacto</h2>
          <p>
            Para más consultas sobre esta herramienta computacional y sus proyecciones,
            <a href="mailto:contacto@centroenergia.cl"> contactar a desarrolladores</a>.
          </p>
        </Section>

        <Section>
          <h2>Referencias de interés</h2>
          <References>
            <li>Centro de Energía, 2019. Desarrollo de una herramienta de modelamiento y simulación de políticas climáticas. Estudio desarrollado para AGCID y Ministerio de Energía.</li>
            <li>Centro de Energía, 2019. Estudio de actualización y complementación de herramientas de prospectivas de Largo Plazo asociadas a la demanda energética. Disponible en <a href="https://www.cne.cl/wp-content/uploads/2020/03/Informe-final.pdf">el siguiente link</a>.</li>
            <li>Carlos Benavides, Manuel Díaz, Raúl O'Ryan, Sebastián Gwinner, Erick Sierra (2021). Methodology to analyse the impact of an emissions trading system in Chile, Climate Policy, 21:8, 1099-1110. <a href="https://doi.org/10.1080/14693062.2021.1954869">Link a revista</a>.</li>
            <li>Centro de Energía, 2021. Comparación de Instrumentos Económicos del Proyecto de Ley Marco de Cambio Climático en base al Modelo Energético PMR. Disponible en <a href="https://4echile.cl/wp-content/uploads/2021/12/Informe-Final-Modelo-PMR.pdf">el siguiente link</a>.</li>
            <li>Carlos Benavides, Manuel Díaz, Rodrigo Palma, et. al, 2021. Opciones para lograr la carbono-neutralidad en Chile: una evaluación bajo incertidumbre. Disponible en <a href="http://dx.doi.org/10.18235/0003527">página del BID</a>.</li>
            <li>Carlos Benavides, Manuel Díaz, Rodrigo Palma, et. al, 2022. Recomendaciones y Pasos Necesarios para Desplegar un Esquema de Instrumentos Económicos Que Catalice la Transición Energética Necesaria para Cumplir con la NDC de Chile y el Objetivo de Cero Emisiones de GEI. <a href="https://documents1.worldbank.org/curated/en/433541647323669007/pdf/Recomendaciones-y-Pasos-Necesarios-para-Desplegar-un-Esquema-de-Instrumentos-Economicos-Que-Catalice-la-Transicion-Energetica-Necesaria-para-Cumplir-con-la-NDC-de-Chile-y-el-Objetivo-de-Cero-Emisiones-de-Gei.pdf">Disponible en página del Banco Mundial</a>.</li>
            <li>Vicente Sepúlveda (2022). Modelo de proyección de escenarios de carbono neutralidad bajo incertidumbre. Memoria de título. Disponible en <a href="https://repositorio.uchile.cl/handle/2250/185729">el siguiente link</a>.</li>
            <li>Diego Cancino (2022). Escenarios de carbono neutralidad para Chile. Memoria de título. Disponible en <a href="https://repositorio.uchile.cl/handle/2250/189406">el siguiente link</a>.</li>
            <li>Maximiliano Donoso (2022). Evaluación de escenarios de uso de hidrógeno en Chile mediante un modelo de planificación energética. Memoria de título. Disponible en <a href="https://repositorio.uchile.cl/handle/2250/189188">el siguiente link</a>.</li>
            <li>Vicente Sepúlveda, Carlos Benavides (2022). Carbon Neutrality Scenario Projection Model under Uncertainty, 2022 IEEE International Conference on Automation/XXV Congress of the Chilean Association of Automatic Control (ICA-ACCA), Curicó, Chile, 2022, pp. 1-6. Disponible en <a href="https://doi.org/10.1109/ICA-ACCA56767.2022.10005931">el siguiente link</a>.</li>
            <li>Centro de Energía, Centro de Ciencia del Clima y la Resiliencia (CR)2 (2019-2025). Taller de simulación en Diplomado de Cambio Climático y Desarrollo Bajo en Carbono. Información del diplomado <a href="https://www.diplomacambioclimatico.cl/">en el siguiente link</a>.</li>
            <li>Centro de Energía, Centro de Ciencia del Clima y la Resiliencia (CR)2 (2022-2025). Observatorio Carbono Neutralidad. Plataforma disponible en <a href="https://observatoriocarbononeutral.cl/">observatoriocarbononeutral.cl</a>.</li>
            <li>Centro de Energía (2025). Apoyo a la elaboración de escenarios de emisiones de gases de efecto invernadero para el anteproyecto de contribución determinada a nivel nacional. Disponible en <a href="https://expedientes.mma.gob.cl/storage/2025/06/06/expedientes/pdf/doc_7_20250606142401.pdf">el siguiente link</a>.</li>
            <li>Centro de Energía, 2026. Modelación y comparación de beneficios e impactos de aplicar instrumentos de precio al carbono en el sector energía. Proyecto desarrollado para la iniciativa PMI, Ministerio de Energía y AGCID.</li>
            <li>Centro de Energía (2026). Curso de Especialización Proyección de escenarios de generación eléctrica con modelo PMR. Información del curso disponible en <a href="https://www.u-cursos.cl/ingenieria/CECENTPMR01/datos_ramo/">el siguiente link</a>.</li>
          </References>
        </Section>
      </Article>
    </Wrapper>
    <Divider variant="middle" />
    <FooterWrap>
      <Footer />
    </FooterWrap>
  </Page>
);

export default AcercaDe;
